import { isEqual } from 'lodash';

import { ResourceType } from '../common/typings';

import { getRecentPaneDefaultFilters, getStockPaneDefaultFilters } from './helpers';
import {
    EmptyGalleryPanesRecord,
    GalleriesState,
    GalleryCollectionItem,
    GalleryPaneData,
    GalleryPaneFilters,
    GalleryPaneTypes,
    GalleryRecentPaneData,
    GalleryRecentPaneFilters,
    GalleryResourceData,
    GalleryStockPaneData,
    GalleryStockPaneFilters
} from './typings';

export const selectCurrentGallery = (
    state: GalleriesState,
    resourceType?: ResourceType
): GalleryResourceData<GalleryCollectionItem> => {
    const { currentResourceType, resources } = state;

    resourceType = resourceType || currentResourceType;

    return resources[resourceType];
};

const paneHasFilters = (galleryType: GalleryPaneTypes, filters: GalleryPaneFilters): boolean => {
    if (galleryType === GalleryPaneTypes.recent) {
        return (
            (filters as GalleryRecentPaneFilters).recentNameFilter !== getRecentPaneDefaultFilters().recentNameFilter
        );
    }

    if (galleryType === GalleryPaneTypes.stock) {
        const { semanticText, selectedGalleryCategories } = filters as GalleryStockPaneFilters;
        const defaultFilters = getStockPaneDefaultFilters();

        return (
            semanticText !== defaultFilters.semanticText ||
            !isEqual(selectedGalleryCategories, defaultFilters.selectedGalleryCategories)
        );
    }

    return false;
};

export const selectGalleryPane = (
    state: GalleriesState,
    galleryType?: GalleryPaneTypes
): GalleryPaneData<GalleryCollectionItem> => {
    const currentGallery = selectCurrentGallery(state);
    const { panes, currentPaneType } = currentGallery;

    galleryType = galleryType || currentPaneType;

    if (panes[galleryType]) {
        return panes[galleryType];
    }

    throw Error(`Gallery pane data for pane ${galleryType} not defined`);
};

export const selectStockGalleryPane = (state: GalleriesState): GalleryStockPaneData<GalleryCollectionItem> => {
    const currentGallery = selectCurrentGallery(state);
    const { panes } = currentGallery;

    return panes[GalleryPaneTypes.stock];
};

export const selectRecentGalleryPane = (state: GalleriesState): GalleryRecentPaneData<GalleryCollectionItem> => {
    const currentGallery = selectCurrentGallery(state);
    const { panes } = currentGallery;

    return panes[GalleryPaneTypes.recent];
};

export const selectCurrentGalleryPaneType = (state: GalleriesState): GalleryPaneTypes => {
    const currentGallery = selectCurrentGallery(state);

    return currentGallery.currentPaneType;
};

export const selectEmptyGalleryPanesMap = (
    state: GalleriesState,
    resourceType?: ResourceType
): EmptyGalleryPanesRecord => {
    const { currentResourceType, resources } = state;
    const resource = resources[currentResourceType];
    const { enabledGalleryTypes } = selectCurrentGallery(state, resourceType);

    const emptyGalleriesMap: EmptyGalleryPanesRecord = {} as EmptyGalleryPanesRecord;

    enabledGalleryTypes.forEach((galleryType: GalleryPaneTypes) => {
        const pane = resource.panes[galleryType];
        const collection = pane.data.collection;
        const { isAllItemsLoading, filters } = pane;
        const hasFilters = paneHasFilters(galleryType, filters);
        // по идее тут нужно смотреть не на collection.length - это количество загруженных элеметов галереи
        // а на total - но сейчас в canvas total не обновляется при удалении элементов, а  collection.length обновляется
        emptyGalleriesMap[galleryType] = collection.length === 0 && !hasFilters && !isAllItemsLoading;
    });

    return emptyGalleriesMap;
};
